import { HttpBackend, HttpRequest, HttpResponse } from '@angular/common/http';
import { Inject, inject, LOCALE_ID } from '@angular/core';
import { TranslateLoader, TranslateService } from '@ngx-translate/core';
import {
  BehaviorSubject,
  filter,
  finalize,
  forkJoin,
  map,
  Observable,
} from 'rxjs';
import { AppConfigService } from '../../services';
import { CUSTOM_LOCALE_FILES, ELocale, ITranslateFile } from '../../models';

/**
 * App Translation Loader
 */
export class AppTranslationLoader implements TranslateLoader {
  private readonly _appConfig = inject(AppConfigService);
  private readonly _httpHandler = inject(HttpBackend);
  private _langLoader$ = new BehaviorSubject<boolean>(false);

  @Inject(LOCALE_ID) private locale!: string;

  get langLoader$() {
    return this._langLoader$.asObservable();
  }

  /**
   * Get Translations.
   * Method used to define translations files that should be used for translations.
   *
   * @param lang - active translations language
   * @returns translations observable
   */
  getTranslation(lang: string): Observable<ITranslateFile> {
    const appVersion = (this._appConfig.config.version || '0.0.0') as string;

    // console.log('=============lang', {
    //   locale: this.locale,
    //   lang,
    //   // bcl: this._translate.getBrowserCultureLang(),
    // });

    const localeFileName =
      CUSTOM_LOCALE_FILES[lang] || CUSTOM_LOCALE_FILES[ELocale.ENGLISH_GB];

    const httpRequestEN = new HttpRequest<ITranslateFile>(
      'GET',
      `/assets/lang/${CUSTOM_LOCALE_FILES[ELocale.ENGLISH_GB]}.json?v=${appVersion}`,
    );

    const forkJoinList = [this._httpHandler.handle(httpRequestEN)];

    const httpRequest = new HttpRequest<ITranslateFile>(
      'GET',
      `/assets/lang/${localeFileName}.json?v=${appVersion}`,
    );

    if (localeFileName !== CUSTOM_LOCALE_FILES[ELocale.ENGLISH_GB]) {
      forkJoinList.push(this._httpHandler.handle(httpRequest));
    }

    this._langLoader$.next(true);
    return forkJoin(forkJoinList).pipe(
      filter(
        (httpEventList) =>
          !httpEventList.find((item) => !(item instanceof HttpResponse)),
      ),
      map((httpResponseList) => {
        let data: HttpResponse<ITranslateFile>['body'] = {};
        httpResponseList.forEach((item) => {
          // eslint-disable-next-line fp/no-mutation
          data = { ...data, ...(item as HttpResponse<ITranslateFile>).body };
        });
        return data;
      }),
      finalize(() => this._langLoader$.next(false)),
    );
  }
}
